import type { Language } from "@/hooks/useLanguage";
import { languageFromPath } from "@/lib/site/publicPages";

// Detect browser language - defaults to English if detection fails
const detectBrowserLanguage = (): Language => {
  if (typeof window === "undefined") return "en";

  try {
    const browserLang = navigator.language.split("-")[0].toLowerCase();
    // Czech, else English: German browsers get English since German was dropped.
    return browserLang === "cs" ? "cs" : "en";
  } catch (error) {
    // If browser language detection fails, default to English
    return "en";
  }
};

/**
 * First render's language: the URL prefix (/cs, /en) wins, then the saved
 * choice, then the browser. Starting from the URL matters: with a stale
 * language on the first commit, LanguageSync would "correct" the URL, so a
 * visitor with English saved (or an English browser, like Googlebot) opening a
 * /cs link got bounced to /en.
 */
export function detectInitialLanguage(): Language {
  if (typeof window !== "undefined") {
    // `/cs/…`, `/en/…`, and the Czech pages at the root of zettl.cz (`/kontakt`).
    const urlLang = languageFromPath(window.location.pathname);
    if (urlLang) return urlLang;
  }
  try {
    const saved = localStorage.getItem("language");
    if (saved === "cs" || saved === "en") return saved;
    // German was dropped: a saved "de" (or anything else) means English.
    if (saved) return "en";
    return detectBrowserLanguage();
  } catch (error) {
    // If localStorage access fails, default to English
    return "en";
  }
}

/**
 * The visitor's explicit language choice as a cookie, so the server-side redirect
 * on `/` (vercel.json) can honour it before Accept-Language; localStorage is
 * invisible to the server. Written only when someone picks a language in a
 * switcher or Settings (chooseLanguage), never on first load or a URL change,
 * which keeps it within the ePrivacy UI-customisation exemption. Values are
 * exactly "cs" or "en". Crawlers send no cookie.
 */
export const LANGUAGE_COOKIE = "zettl_lang";

export function languageCookie(language: Language): string {
  return `${LANGUAGE_COOKIE}=${language}; path=/; max-age=31536000; samesite=lax; secure`;
}

export function persistLanguageCookie(language: Language): void {
  try {
    document.cookie = languageCookie(language);
  } catch {
    // No document (prerender) or cookies blocked: the redirect falls back to Accept-Language.
  }
}
